const all = new Map<string, GeckoSource>()

/**
 * An abstract representation of the source of the data: either a "nominal"
 * written down in a spec somewhere, an inspection type (RUG, RAUT, etc.),
 * or a "projection" into the future.
 */
export class GeckoSource {
  //----------------------------------------------------------------------------

  public static readonly Description = 'How data was collected'

  //----------------------------------------------------------------------------

  public static readonly RUG = new GeckoSource(
    'rug',
    'RUG',
    'RUG (Rapid Ultrasonic Gridding) inspection'
  )
  public static readonly RAUT = new GeckoSource(
    'raut',
    'RAUT',
    'RAUT (Rapid Automated Ultrasonic Testing) inspection'
  )
  public static readonly TRILAT = new GeckoSource(
    'trilat',
    'TRILAT',
    'Data sourced from a TriLateral inspection'
  )
  public static readonly Lidar = new GeckoSource(
    'lidar',
    'Lidar',
    'Lidar inspection'
  )
  public static readonly PhasedArray = new GeckoSource(
    'phased-array',
    'Phased Array',
    'Phased Array inspection'
  )
  public static readonly TriLat = new GeckoSource(
    'trilat',
    'TriLat',
    'TriLat inspection'
  )
  public static readonly IE = new GeckoSource(
    'ie',
    'IE',
    'IE (Impact Echo) inspection'
  )
  public static readonly SASW = new GeckoSource(
    'sasw',
    'SASW',
    'SASW (Spectral Analysis of Surface Waves) inspection'
  )
  public static readonly UPE = new GeckoSource(
    'upe',
    'UPE',
    'UPE (Ultrasonic Pulse Echo) inspection'
  )
  public static readonly Manual = new GeckoSource(
    'manual',
    'Manual',
    'Manual NDT inspection'
  )
  public static readonly EMAT = new GeckoSource(
    'emat',
    'EMAT',
    'EMAT (Electro Magnetic Acoustic Transducer) inspection'
  )
  public static readonly MI = new GeckoSource(
    'mi',
    'MI',
    'MI (Magnetic Induction) inspection'
  )
  public static readonly ART = new GeckoSource(
    'art',
    'ART',
    'ART (Acoustic Resonance Technology) inspection'
  )

  public static readonly PEC = new GeckoSource(
    'pulsed-eddy-current',
    'PEC',
    'Pulsed Eddy Current inspection'
  )

  public static readonly PERCENT_ASCANS_BELOW_TMIN = new GeckoSource(
    'percent-ascans-below-tmin',
    'PERCENT_ASCANS_BELOW_TMIN',
    'The percentage of ascans in a bin that are below tmin'
  )

  //----------------------------------------------------------------------------

  public static readonly Nominal: GeckoSource = new GeckoSource(
    'nominal',
    'Nominal',
    'Expected, as-built surface thickness'
  )
  public static readonly TMin: GeckoSource = new GeckoSource(
    'tmin',
    'T-Min',
    'Minimum allowable surface thickness'
  )
  public static readonly Projection = new GeckoSource(
    'projection',
    'Projection',
    'derived'
  )

  // This is a temporary source, which will be removed once all derivative CDFs are generated "on-demand"
  public static readonly OnDemand = new GeckoSource(
    'on-demand',
    'On-Demand',
    'Calculations done in Forecast'
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific data source */
  public readonly description: string

  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for component data frame sources
   * @param a The first source object
   * @param b The second source object
   */
  public static DefaultSort(a: GeckoSource, b: GeckoSource): number {
    return defaultSortOrder.indexOf(a.id) - defaultSortOrder.indexOf(b.id)
  }

  /**
   * Returns the unique data source instance for a given ID if it exists
   * @param id The unique ID of this data source
   */
  public static GetByID(id: string | undefined): GeckoSource | undefined {
    return all.get(id ?? '')
  }

  /**
   * Returns the unique data source instance for a given ID, or creates a
   * unique data source instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string): GeckoSource {
    return all.get(id) ?? new GeckoSource(id, id, '')
  }
}

const defaultSortOrder = [
  // Inspection sources
  GeckoSource.RUG.id,
  GeckoSource.RAUT.id,
  GeckoSource.Lidar.id,
  GeckoSource.PhasedArray.id,
  GeckoSource.TriLat.id,
  GeckoSource.IE.id,
  GeckoSource.SASW.id,
  GeckoSource.UPE.id,
  GeckoSource.Manual.id,
  GeckoSource.EMAT.id,
  GeckoSource.MI.id,
  GeckoSource.ART.id,
  GeckoSource.PEC.id,
  // Other sources
  GeckoSource.Nominal.id,
  GeckoSource.TMin.id,
  GeckoSource.Projection.id,
  GeckoSource.OnDemand.id,
]
